import { useState, useEffect, useRef } from 'react'
import * as Cesium from 'cesium'
import 'cesium/Build/Cesium/Widgets/widgets.css'
import axios from 'axios'

// Cesium Ion を使用しない設定
Cesium.Ion.defaultAccessToken = undefined as any

// GeoJSON型定義
interface RawData {
  landuse_type?: string
  population_density?: number
  nearest_hospital_distance?: number
  nearest_station_distance?: number
  building_max_height?: number
  building_density?: number
  restriction_evaluation?: string
  restriction_recommendation?: string
  directions_clear?: number
  data_sources?: string[]
  api_errors?: string[]
  flood_risk?: boolean
  landslide_risk?: boolean
}

interface FeatureProperties {
  rank?: string
  total_score?: number
  center_lat?: number
  center_lon?: number
  excluded?: boolean
  exclusion_reason?: string
  scores?: Record<string, number>
  raw_data?: RawData
}

interface GeoJSONFeature {
  type: 'Feature'
  geometry: {
    type: string
    coordinates: number[] | number[][] | number[][][]
  }
  properties: FeatureProperties | null
}

interface GeoJSONFeatureCollection {
  type: 'FeatureCollection'
  features: GeoJSONFeature[]
}

// 型定義
interface Weights {
  restriction_surface: number
  landuse: number
  downwash: number
  population: number
  emergency_facility: number
  access: number
}

interface AnalysisResult {
  analysis_id: string
  status: string
  summary: {
    total_meshes: number
    analyzed_meshes: number
    excluded_meshes: number
    rank_distribution: {
      S: number
      A: number
      B: number
      C: number
      D: number
    }
  }
  result: GeoJSONFeatureCollection
}

// 定数
const TSUKUBA_CENTER = Cesium.Cartesian3.fromDegrees(140.1, 36.08, 5000)
const INITIAL_ORIENTATION = {
  heading: Cesium.Math.toRadians(0),
  pitch: Cesium.Math.toRadians(-45),
  roll: 0
}

const RANK_COLORS: Record<string, string> = {
  S: '#2E7D32',
  A: '#7CB342',
  B: '#FDD835',
  C: '#FB8C00',
  D: '#D32F2F',
  X: '#9E9E9E'
}

const RANK_LABELS: Record<string, string> = {
  S: '最適',
  A: '適',
  B: '可',
  C: '難',
  D: '不適',
  X: '除外'
}

// スコアの判定理由を生成する関数
const getScoreReason = (key: string, score: number, rawData?: RawData): string => {
  switch (key) {
    case 'restriction_surface':
      if (rawData?.directions_clear !== undefined) {
        if (rawData.directions_clear >= 2) {
          return `進入方向が${rawData.directions_clear}方向確保されており、制限表面の確保が容易です。`
        } else if (rawData.directions_clear === 1) {
          return `進入方向が1方向のみ確保。制限表面の確保にやや制約があります。`
        }
      }
      if (rawData?.building_max_height !== undefined) {
        if (rawData.building_max_height < 30) {
          return `周辺建物が低く（最大${Math.round(rawData.building_max_height)}m）、制限表面の確保が比較的容易です。`
        } else if (rawData.building_max_height < 50) {
          return `周辺に中層建物あり（最大${Math.round(rawData.building_max_height)}m）。制限表面の確保に注意が必要です。`
        } else {
          return `周辺に高層建物あり（最大${Math.round(rawData.building_max_height)}m）。制限表面の確保が困難な可能性があります。`
        }
      }
      return score >= 70 ? '周辺環境から制限表面の確保が可能と判定。' : '周辺環境により制限表面の確保に制約があります。'

    case 'landuse':
      if (rawData?.landuse_type) {
        if (rawData.landuse_type.includes('工業')) {
          return `${rawData.landuse_type}は騒音・振動への許容度が高く、バーティポートに最適です。`
        } else if (rawData.landuse_type.includes('商業')) {
          return `${rawData.landuse_type}は利便性が高いですが、周辺への配慮が必要です。`
        } else if (rawData.landuse_type.includes('住居')) {
          return `${rawData.landuse_type}は騒音規制が厳しく、住民への影響を考慮する必要があります。`
        } else if (rawData.landuse_type.includes('調整')) {
          return `${rawData.landuse_type}は開発規制がありますが、適切な手続きにより設置可能な場合があります。`
        }
        return `${rawData.landuse_type}での適合性を評価しています。`
      }
      return '用途地域情報に基づいて適合性を評価。'

    case 'downwash':
      if (rawData?.landuse_type) {
        if (rawData.landuse_type.includes('工業')) {
          return '工業系地域のため、歩行者への影響が限定的です。'
        } else if (rawData.landuse_type.includes('商業')) {
          return '商業地域のため、歩行者動線への影響に注意が必要です。'
        } else if (rawData.landuse_type.includes('住居')) {
          return '住居系地域のため、生活者へのダウンウォッシュ影響を考慮する必要があります。'
        }
      }
      return score >= 70 ? '周辺への気流影響が限定的と判定。' : '周辺への気流影響に配慮が必要です。'

    case 'population':
      if (rawData?.population_density !== undefined) {
        const density = Math.round(rawData.population_density)
        if (density < 500) {
          return `人口密度が低く（${density}人/km²）、騒音影響を受ける住民が少ないです。`
        } else if (density < 1500) {
          return `人口密度がやや低め（${density}人/km²）で、騒音影響は限定的です。`
        } else if (density < 3000) {
          return `中程度の人口密度（${density}人/km²）。騒音対策を検討してください。`
        } else if (density < 5000) {
          return `人口密度が高め（${density}人/km²）。騒音影響への十分な配慮が必要です。`
        } else {
          return `人口密度が高く（${density}人/km²）、騒音影響を受ける住民が多いため注意が必要です。`
        }
      }
      return '周辺人口に基づく騒音影響を評価。'

    case 'emergency_facility':
      if (rawData?.nearest_hospital_distance !== undefined) {
        const dist = Math.round(rawData.nearest_hospital_distance)
        if (dist <= 1000) {
          return `最寄り病院まで${dist}mと近く、緊急時の対応に優れています。`
        } else if (dist <= 2000) {
          return `最寄り病院まで${dist}m。緊急時のアクセスは比較的良好です。`
        } else if (dist <= 3000) {
          return `最寄り病院まで${dist}m。緊急時のアクセスは標準的です。`
        } else {
          return `最寄り病院まで${dist}mとやや遠く、緊急時の対応に課題があります。`
        }
      }
      return '緊急医療施設へのアクセス性を評価。'

    case 'access':
      if (rawData?.nearest_station_distance !== undefined) {
        const dist = Math.round(rawData.nearest_station_distance)
        if (dist <= 500) {
          return `最寄り駅まで${dist}mと非常に近く、利用者のアクセスに優れています。`
        } else if (dist <= 1000) {
          return `最寄り駅まで${dist}m。利用者のアクセスは良好です。`
        } else if (dist <= 1500) {
          return `最寄り駅まで${dist}m。徒歩でのアクセスが可能な範囲です。`
        } else {
          return `最寄り駅まで${dist}m。公共交通との連携に課題があります。`
        }
      }
      return '公共交通へのアクセス性を評価。'

    default:
      return ''
  }
}

// 総合判定理由を生成する関数
const getOverallReason = (rank: string, scores: Record<string, number>, _rawData?: RawData): string => {
  const strongPoints: string[] = []
  const weakPoints: string[] = []

  Object.entries(scores).forEach(([key, score]) => {
    const labels: Record<string, string> = {
      restriction_surface: '制限表面確保',
      landuse: '用途地域適合',
      downwash: 'ダウンウォッシュ',
      population: '騒音影響',
      emergency_facility: '緊急施設',
      access: '交通アクセス'
    }
    if (score >= 80) {
      strongPoints.push(labels[key])
    } else if (score < 40) {
      weakPoints.push(labels[key])
    }
  })

  let reason = ''
  switch (rank) {
    case 'S':
      reason = 'バーティポートとして最適な立地です。'
      if (strongPoints.length > 0) {
        reason += `特に${strongPoints.slice(0, 3).join('、')}の評価が高く、安全かつ効率的な運用が期待できます。`
      }
      break
    case 'A':
      reason = 'バーティポートとして適した立地です。'
      if (strongPoints.length > 0) {
        reason += `${strongPoints.slice(0, 2).join('、')}に優れています。`
      }
      if (weakPoints.length > 0) {
        reason += `${weakPoints[0]}については改善の余地があります。`
      }
      break
    case 'B':
      reason = '条件付きでバーティポートの設置が可能な立地です。'
      if (weakPoints.length > 0) {
        reason += `${weakPoints.slice(0, 2).join('、')}について対策を検討してください。`
      }
      break
    case 'C':
      reason = 'バーティポートの設置には課題がある立地です。'
      if (weakPoints.length > 0) {
        reason += `${weakPoints.slice(0, 2).join('、')}に大きな課題があり、設置には相当の対策が必要です。`
      }
      break
    case 'D':
      reason = 'バーティポートの設置には適さない立地です。'
      if (weakPoints.length > 0) {
        reason += `${weakPoints.slice(0, 3).join('、')}など複数の評価項目で課題があります。`
      }
      reason += '他の候補地を検討することを推奨します。'
      break
    default:
      reason = '評価対象外の地点です。'
  }

  return reason
}

// PLATEAU 3D Tiles URLs (つくば市)
const PLATEAU_3DTILES = {
  lod1: 'https://assets.cms.plateau.reearth.io/assets/e5/5fef61-cfe5-4132-bc72-5f46fc8d0ea9/08220_tsukuba-shi_city_2023_citygml_2_op_bldg_3dtiles_lod1/tileset.json',
  lod2: 'https://assets.cms.plateau.reearth.io/assets/44/60c228-5d7c-426d-8cbd-13407ee83578/08220_tsukuba-shi_city_2023_citygml_2_op_bldg_3dtiles_lod2/tileset.json'
}

// 背景地図オプション
const BASE_LAYERS = {
  pale: {
    url: 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
    name: '淡色地図',
    minLevel: 2,
    maxLevel: 18
  },
  standard: {
    url: 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png',
    name: '標準地図',
    minLevel: 2,
    maxLevel: 18
  },
  photo: {
    url: 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg',
    name: '航空写真',
    minLevel: 10,  // 航空写真は高ズームレベルのみ
    maxLevel: 18
  }
}

type BaseLayerType = keyof typeof BASE_LAYERS

// 建物表示の高度閾値（この高度以下で表示）
const BUILDING_SHOW_ALTITUDE = 3000 // メートル

// 分析範囲の定義（つくば駅周辺に絞る - 高速化のため狭い範囲）
const DEFAULT_BBOX = {
  min_lon: 140.08,
  min_lat: 36.07,
  max_lon: 140.12,
  max_lat: 36.10
}

function App() {
  const viewerRef = useRef<Cesium.Viewer | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const tilesetRef = useRef<Cesium.Cesium3DTileset | null>(null)
  const geoJsonDataSourceRef = useRef<Cesium.GeoJsonDataSource | null>(null)
  const bboxEntityRef = useRef<Cesium.Entity | null>(null)

  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [bbox, _setBbox] = useState(DEFAULT_BBOX)
  const [showBbox, setShowBbox] = useState(true)
  const [weights, setWeights] = useState<Weights>({
    restriction_surface: 0.25,
    landuse: 0.20,
    downwash: 0.15,
    population: 0.15,
    emergency_facility: 0.15,
    access: 0.10
  })
  const [exclusion, setExclusion] = useState({
    flood_area: true,
    landslide_area: true,
    natural_park: true,
    aviation_restriction: true
  })
  const [meshSize, setMeshSize] = useState(200)
  const [dValue, setDValue] = useState(15)
  const [fastMode, setFastMode] = useState(true)  // 高速モード（デフォルトON）
  const [selectedFeature, setSelectedFeature] = useState<GeoJSONFeature | null>(null)
  const [_popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null)
  const [showBuildings, setShowBuildings] = useState(true)
  const [buildingLod, setBuildingLod] = useState<'lod1' | 'lod2'>('lod1')
  const [viewerReady, setViewerReady] = useState(false)
  const [cameraAltitude, setCameraAltitude] = useState(5000)
  const [baseLayer, setBaseLayer] = useState<BaseLayerType>('pale')

  // Cesium Viewer初期化
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return

    // 国土地理院タイルプロバイダー
    const gsiProvider = new Cesium.UrlTemplateImageryProvider({
      url: 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
      credit: new Cesium.Credit('国土地理院'),
      minimumLevel: 2,
      maximumLevel: 18
    })

    const viewer = new Cesium.Viewer(containerRef.current, {
      timeline: false,
      animation: false,
      homeButton: false,
      baseLayerPicker: false,
      navigationHelpButton: false,
      sceneModePicker: false,
      geocoder: false,
      fullscreenButton: false,
      vrButton: false,
      infoBox: false,
      selectionIndicator: false,
      baseLayer: new Cesium.ImageryLayer(gsiProvider)
    })

    // 地形データを設定（ArcGIS World Elevation）
    Cesium.ArcGISTiledElevationTerrainProvider.fromUrl(
      'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer'
    ).then(terrainProvider => {
      if (viewer && !viewer.isDestroyed()) {
        viewer.terrainProvider = terrainProvider
        viewer.scene.globe.depthTestAgainstTerrain = true
      }
    }).catch(err => {
      console.warn('Failed to load terrain:', err)
    })

    viewerRef.current = viewer

    // グローブ設定
    viewer.scene.globe.enableLighting = false
    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString('#e0e0e0')

    // カメラコントローラー設定（すべての操作を有効化）
    const controller = viewer.scene.screenSpaceCameraController
    controller.enableRotate = true      // 回転
    controller.enableTranslate = true   // 平行移動
    controller.enableZoom = true        // ズーム
    controller.enableTilt = true        // 傾き
    controller.enableLook = true        // 視点回転
    controller.minimumZoomDistance = 10   // 最小ズーム距離（より近くまで寄れる）
    controller.maximumZoomDistance = 50000 // 最大ズーム距離

    // 初期カメラ位置設定
    viewer.camera.setView({
      destination: TSUKUBA_CENTER,
      orientation: INITIAL_ORIENTATION
    })

    // カメラ高度監視
    viewer.camera.changed.addEventListener(() => {
      const cartographic = viewer.camera.positionCartographic
      if (cartographic) {
        setCameraAltitude(cartographic.height)
      }
    })

    // クリックイベント設定
    viewer.screenSpaceEventHandler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
      // すべてのオブジェクトをピック（深度に関係なく）
      const pickedObjects = viewer.scene.drillPick(movement.position)
      console.log('Picked objects:', pickedObjects.length, pickedObjects)

      // 分析メッシュのエンティティを探す
      for (const pickedObject of pickedObjects) {
        if (Cesium.defined(pickedObject) && pickedObject.id && pickedObject.id.properties) {
          const properties = pickedObject.id.properties
          // rankプロパティがあれば分析メッシュ
          if (properties.rank !== undefined) {
            const extractedProps = {
              rank: properties.rank?.getValue(),
              total_score: properties.total_score?.getValue(),
              center_lat: properties.center_lat?.getValue(),
              center_lon: properties.center_lon?.getValue(),
              excluded: properties.excluded?.getValue(),
              exclusion_reason: properties.exclusion_reason?.getValue(),
              scores: properties.scores?.getValue(),
              raw_data: properties.raw_data?.getValue()
            }
            console.log('Extracted properties:', extractedProps)
            const feature: GeoJSONFeature = {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [0, 0] },
              properties: extractedProps
            }
            setSelectedFeature(feature)
            // ポップアップ位置を設定（クリック位置）
            setPopupPosition({ x: movement.position.x, y: movement.position.y })
            console.log('Selected feature set:', feature)
            return
          }
        }
      }
      // 分析メッシュが見つからなければ選択解除
      setSelectedFeature(null)
      setPopupPosition(null)
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    setViewerReady(true)

    return () => {
      if (viewer && !viewer.isDestroyed()) {
        viewer.destroy()
      }
      viewerRef.current = null
    }
  }, [])

  // 背景地図の切り替え
  useEffect(() => {
    if (!viewerRef.current || !viewerReady) return

    const viewer = viewerRef.current
    const layers = viewer.imageryLayers

    // 既存のレイヤーを削除
    layers.removeAll()

    // 新しいレイヤーを追加
    const layerConfig = BASE_LAYERS[baseLayer]
    const provider = new Cesium.UrlTemplateImageryProvider({
      url: layerConfig.url,
      credit: new Cesium.Credit('国土地理院'),
      minimumLevel: layerConfig.minLevel,
      maximumLevel: layerConfig.maxLevel
    })
    layers.addImageryProvider(provider)
  }, [baseLayer, viewerReady])

  // 建物タイルセットの読み込み（カメラが近い時のみ読み込む）
  useEffect(() => {
    if (!viewerRef.current || !viewerReady) return

    const viewer = viewerRef.current
    const shouldLoad = showBuildings && cameraAltitude < BUILDING_SHOW_ALTITUDE

    // カメラが遠い場合はタイルセットを削除してメモリを解放
    if (!shouldLoad) {
      if (tilesetRef.current) {
        console.log('Removing tileset (camera too far):', buildingLod)
        viewer.scene.primitives.remove(tilesetRef.current)
        tilesetRef.current = null
      }
      return
    }

    // 既に同じLODのタイルセットが読み込まれている場合は何もしない
    if (tilesetRef.current) {
      return
    }

    let cancelled = false
    const url = PLATEAU_3DTILES[buildingLod]
    console.log('Loading tileset (camera close):', buildingLod, url)

    Cesium.Cesium3DTileset.fromUrl(url).then(tileset => {
      if (cancelled) {
        // キャンセルされた場合は追加しない
        console.log('Tileset load cancelled:', buildingLod)
        return
      }

      tileset.style = new Cesium.Cesium3DTileStyle({
        color: "color('white', 0.8)",
        show: true
      })

      // 高さオフセットのみ適用（水平方向のずれを防ぐ）
      const heightOffset = -40 // ジオイド高（メートル）
      const cartographic = Cesium.Cartographic.fromDegrees(140.1, 36.08, 0)
      const surfaceNormal = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(
        Cesium.Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude)
      )
      const offsetVector = Cesium.Cartesian3.multiplyByScalar(
        surfaceNormal,
        heightOffset,
        new Cesium.Cartesian3()
      )
      tileset.modelMatrix = Cesium.Matrix4.fromTranslation(offsetVector)

      // 古いタイルセットがあれば削除
      if (tilesetRef.current) {
        viewer.scene.primitives.remove(tilesetRef.current)
      }

      viewer.scene.primitives.add(tileset)
      tilesetRef.current = tileset

      console.log('Tileset loaded and added:', buildingLod)
    }).catch(error => {
      console.error('Failed to load tileset:', error)
    })

    return () => {
      cancelled = true
    }
  }, [showBuildings, buildingLod, viewerReady, cameraAltitude])

  // 分析結果のGeoJSON表示
  useEffect(() => {
    if (!viewerRef.current || !viewerReady) return

    const viewer = viewerRef.current

    // 既存のデータソースを削除
    if (geoJsonDataSourceRef.current) {
      viewer.dataSources.remove(geoJsonDataSourceRef.current)
      geoJsonDataSourceRef.current = null
    }

    if (analysisResult) {
      Cesium.GeoJsonDataSource.load(analysisResult.result as any, {
        clampToGround: false
      }).then(dataSource => {
        viewer.dataSources.add(dataSource)
        geoJsonDataSourceRef.current = dataSource

        // スタイル設定 - 建物より上に表示するため高度を設定
        dataSource.entities.values.forEach(entity => {
          if (entity.polygon && entity.properties) {
            const rank = entity.properties.rank?.getValue() || 'X'
            const color = RANK_COLORS[rank] || '#9E9E9E'

            entity.polygon.material = Cesium.Color.fromCssColorString(color).withAlpha(0.8) as any
            entity.polygon.outline = true as any
            entity.polygon.outlineColor = Cesium.Color.WHITE as any
            entity.polygon.outlineWidth = 2 as any
            // 絶対高度で設定（ピックしやすくするため）
            entity.polygon.height = 80 as any
            entity.polygon.extrudedHeight = 85 as any  // 少し厚みを持たせてクリックしやすく
            entity.polygon.heightReference = Cesium.HeightReference.NONE as any
            entity.polygon.extrudedHeightReference = Cesium.HeightReference.NONE as any
          }
        })

        // 結果範囲にズーム
        viewer.zoomTo(dataSource)
      }).catch(error => {
        console.error('Failed to load GeoJSON:', error)
      })
    }
  }, [analysisResult, viewerReady])

  // 分析範囲の矩形を表示
  useEffect(() => {
    if (!viewerRef.current || !viewerReady) return

    const viewer = viewerRef.current

    // 既存の範囲表示を削除
    if (bboxEntityRef.current) {
      viewer.entities.remove(bboxEntityRef.current)
      bboxEntityRef.current = null
    }

    if (showBbox) {
      const entity = viewer.entities.add({
        name: '分析範囲',
        rectangle: {
          coordinates: Cesium.Rectangle.fromDegrees(
            bbox.min_lon,
            bbox.min_lat,
            bbox.max_lon,
            bbox.max_lat
          ),
          material: Cesium.Color.BLUE.withAlpha(0.1),
          outline: true,
          outlineColor: Cesium.Color.BLUE,
          outlineWidth: 3,
          height: 0,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
        }
      })
      bboxEntityRef.current = entity
    }
  }, [bbox, showBbox, viewerReady])

  // 分析実行
  const runAnalysis = async () => {
    setLoading(true)
    try {
      // 高速モードかどうかでエンドポイントを切り替え
      const endpoint = fastMode
        ? '/api/v1/analysis/suitability-fast'
        : '/api/v1/analysis/suitability'

      const response = await axios.post<AnalysisResult>(endpoint, {
        bbox: bbox,
        mesh_size: meshSize,
        d_value: dValue,
        weights,
        exclusion
      })
      setAnalysisResult(response.data)
      setSelectedFeature(null)
    } catch (error) {
      console.error('Analysis failed:', error)
      alert('分析に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  // 重みのリセット
  const resetWeights = () => {
    setWeights({
      restriction_surface: 0.25,
      landuse: 0.20,
      downwash: 0.15,
      population: 0.15,
      emergency_facility: 0.15,
      access: 0.10
    })
  }

  // カメラをつくば市中心にリセット
  const resetCamera = () => {
    if (viewerRef.current) {
      viewerRef.current.camera.flyTo({
        destination: TSUKUBA_CENTER,
        orientation: INITIAL_ORIENTATION,
        duration: 1.5
      })
    }
  }

  return (
    <div className="app-container">
      {/* ヘッダー */}
      <header className="header">
        <h1>つくば市バーティポート適地分析GIS</h1>
        <div className="header-buttons">
          {analysisResult && (
            <button
              className="header-btn header-btn-clear"
              onClick={() => {
                setAnalysisResult(null)
                setSelectedFeature(null)
                setPopupPosition(null)
              }}
              title="分析結果を解除"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              分析解除
            </button>
          )}
          <button className="header-btn" onClick={resetCamera}>
            初期位置に戻る
          </button>
          <button className="header-btn" onClick={() => window.open('/api/docs', '_blank')}>
            API仕様
          </button>
        </div>
      </header>

      {/* メインコンテンツ */}
      <div className="main-content">
        {/* サイドパネル */}
        <div className="side-panel">
          {/* 背景地図 */}
          <div className="panel-section">
            <div className="panel-section-header">背景地図</div>
            <div className="panel-section-content">
              {Object.entries(BASE_LAYERS).map(([key, layer]) => (
                <label key={key} className="layer-item">
                  <input
                    type="radio"
                    name="baseLayer"
                    checked={baseLayer === key}
                    onChange={() => setBaseLayer(key as BaseLayerType)}
                  />
                  {layer.name}
                </label>
              ))}
            </div>
          </div>

          {/* 3Dレイヤー設定 */}
          <div className="panel-section">
            <div className="panel-section-header">3Dレイヤー</div>
            <div className="panel-section-content">
              <label className="layer-item">
                <input
                  type="checkbox"
                  checked={showBuildings}
                  onChange={(e) => setShowBuildings(e.target.checked)}
                />
                PLATEAU建物モデル
              </label>
              <p style={{ fontSize: '11px', color: '#666', marginLeft: '26px' }}>
                高度{BUILDING_SHOW_ALTITUDE/1000}km以下で表示
              </p>
              <p style={{ fontSize: '11px', color: cameraAltitude < BUILDING_SHOW_ALTITUDE ? '#2E7D32' : '#999', marginLeft: '26px' }}>
                現在: {(cameraAltitude/1000).toFixed(1)}km {cameraAltitude < BUILDING_SHOW_ALTITUDE ? '(表示中)' : '(非表示)'}
              </p>
              {showBuildings && (
                <div style={{ marginLeft: '26px', marginTop: '8px' }}>
                  <label className="layer-item">
                    <input
                      type="radio"
                      name="buildingLod"
                      checked={buildingLod === 'lod1'}
                      onChange={() => setBuildingLod('lod1')}
                    />
                    LOD1（簡易）
                  </label>
                  <label className="layer-item">
                    <input
                      type="radio"
                      name="buildingLod"
                      checked={buildingLod === 'lod2'}
                      onChange={() => setBuildingLod('lod2')}
                    />
                    LOD2（詳細）
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* 分析範囲 */}
          <div className="panel-section">
            <div className="panel-section-header">分析範囲</div>
            <div className="panel-section-content">
              <label className="layer-item">
                <input
                  type="checkbox"
                  checked={showBbox}
                  onChange={(e) => setShowBbox(e.target.checked)}
                />
                地図上に範囲を表示
              </label>
              <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>南西:</strong> {bbox.min_lat.toFixed(4)}, {bbox.min_lon.toFixed(4)}
                </div>
                <div>
                  <strong>北東:</strong> {bbox.max_lat.toFixed(4)}, {bbox.max_lon.toFixed(4)}
                </div>
              </div>
              <p style={{ fontSize: '10px', color: '#999', marginTop: '8px' }}>
                青い矩形が分析対象エリアです
              </p>
            </div>
          </div>

          {/* 分析条件 */}
          <div className="panel-section">
            <div className="panel-section-header">分析条件</div>
            <div className="panel-section-content">
              <div className="slider-container">
                <div className="slider-label">
                  <span>メッシュサイズ</span>
                  <span>{meshSize}m</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="25"
                  value={meshSize}
                  onChange={(e) => setMeshSize(Number(e.target.value))}
                />
                <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                  分析の単位となるグリッドのサイズ
                </div>
              </div>

              <div className="slider-container" style={{ marginTop: '12px' }}>
                <div className="slider-label">
                  <span>想定機体D値</span>
                  <span>{dValue}m</span>
                </div>
                <input
                  type="range"
                  min="8"
                  max="25"
                  value={dValue}
                  onChange={(e) => setDValue(Number(e.target.value))}
                />
                <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                  機体の最大寸法（ローター直径）。制限表面の計算に使用
                </div>
                <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                  小型機: 8-12m / 中型機: 12-18m / 大型機: 18-25m
                </div>
              </div>

              <h4 style={{ fontSize: '13px', marginTop: '16px', marginBottom: '8px' }}>評価指標の重み</h4>
              <p style={{ fontSize: '10px', color: '#666', marginBottom: '12px' }}>
                重みが高いほど総合スコアへの影響が大きくなります
              </p>

              {[
                {
                  key: 'restriction_surface',
                  label: '制限表面確保',
                  description: '離着陸時の安全な飛行経路を確保できるか',
                  highEffect: '周辺に高い建物がない場所を重視',
                  lowEffect: '建物の高さをあまり考慮しない'
                },
                {
                  key: 'landuse',
                  label: '用途地域適合',
                  description: '都市計画法上の用途地域との適合性',
                  highEffect: '工業地域など騒音に寛容な地域を重視',
                  lowEffect: '住居地域でも候補に含める'
                },
                {
                  key: 'downwash',
                  label: 'ダウンウォッシュ',
                  description: '機体の下降気流が歩行者に与える影響',
                  highEffect: '歩行者が少ないエリアを重視',
                  lowEffect: '歩行者への影響をあまり考慮しない'
                },
                {
                  key: 'population',
                  label: '騒音影響',
                  description: '周辺住民への騒音影響（人口密度で評価）',
                  highEffect: '人口が少ないエリアを重視',
                  lowEffect: '人口密度をあまり考慮しない'
                },
                {
                  key: 'emergency_facility',
                  label: '緊急施設',
                  description: '病院・消防署への近接性（緊急時対応）',
                  highEffect: '医療施設に近い場所を重視',
                  lowEffect: '緊急施設との距離をあまり考慮しない'
                },
                {
                  key: 'access',
                  label: '交通アクセス',
                  description: '駅・主要道路への近接性（利便性）',
                  highEffect: '公共交通に近い場所を重視',
                  lowEffect: '交通アクセスをあまり考慮しない'
                }
              ].map(({ key, label, description, highEffect, lowEffect }) => (
                <div key={key} className="slider-container" style={{ marginBottom: '16px' }}>
                  <div className="slider-label">
                    <span>{label}</span>
                    <span>{Math.round(weights[key as keyof Weights] * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={weights[key as keyof Weights] * 100}
                    onChange={(e) => setWeights({
                      ...weights,
                      [key]: Number(e.target.value) / 100
                    })}
                  />
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                    {description}
                  </div>
                  <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                    高: {highEffect} / 低: {lowEffect}
                  </div>
                </div>
              ))}

              <h4 style={{ fontSize: '13px', marginTop: '16px', marginBottom: '8px' }}>除外条件</h4>
              {[
                { key: 'flood_area', label: '洪水浸水想定区域' },
                { key: 'landslide_area', label: '土砂災害警戒区域' },
                { key: 'natural_park', label: '自然公園地域' },
                { key: 'aviation_restriction', label: '航空法制限区域' }
              ].map(({ key, label }) => (
                <label key={key} className="layer-item">
                  <input
                    type="checkbox"
                    checked={exclusion[key as keyof typeof exclusion]}
                    onChange={(e) => setExclusion({
                      ...exclusion,
                      [key]: e.target.checked
                    })}
                  />
                  {label}
                </label>
              ))}

              <div style={{
                background: fastMode ? '#e8f5e9' : '#fff3e0',
                border: `1px solid ${fastMode ? '#4caf50' : '#ff9800'}`,
                borderRadius: '4px',
                padding: '10px',
                marginTop: '16px',
                marginBottom: '12px'
              }}>
                <label className="layer-item" style={{ marginBottom: '4px' }}>
                  <input
                    type="checkbox"
                    checked={fastMode}
                    onChange={(e) => setFastMode(e.target.checked)}
                  />
                  <strong>高速モード</strong>
                </label>
                <p style={{ fontSize: '10px', color: '#666', marginLeft: '26px' }}>
                  {fastMode
                    ? '事前キャッシュデータを使用（即時完了）'
                    : '外部APIから最新データを取得（数十秒〜数分）'
                  }
                </p>
              </div>

              <button className="analyze-btn" onClick={runAnalysis} disabled={loading}>
                {loading ? '分析中...' : (fastMode ? '高速分析実行' : '詳細分析実行')}
              </button>
              <button className="reset-btn" onClick={resetWeights}>
                条件リセット
              </button>
            </div>
          </div>

          {/* 凡例 */}
          <div className="panel-section">
            <div className="panel-section-header">凡例</div>
            <div className="panel-section-content">
              {Object.entries(RANK_COLORS).map(([rank, color]) => (
                <div key={rank} className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: color }}></div>
                  <span>{rank}: {RANK_LABELS[rank]} {rank !== 'X' && `(${rank === 'S' ? '80-100' : rank === 'A' ? '60-79' : rank === 'B' ? '40-59' : rank === 'C' ? '20-39' : '0-19'}点)`}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 分析結果サマリー */}
          {analysisResult && (
            <div className="panel-section">
              <div className="panel-section-header">分析結果</div>
              <div className="panel-section-content">
                <div style={{
                  background: '#fff3e0',
                  border: '1px solid #ffb74d',
                  borderRadius: '4px',
                  padding: '8px',
                  marginBottom: '12px',
                  fontSize: '11px',
                  color: '#e65100'
                }}>
                  AIによる自動判定の為、参考にしてください
                </div>
                <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                  分析ID: {analysisResult.analysis_id}
                </p>
                <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                  総メッシュ数: {analysisResult.summary.total_meshes}
                </p>
                <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                  分析メッシュ: {analysisResult.summary.analyzed_meshes}
                </p>
                <p style={{ fontSize: '13px', marginBottom: '12px' }}>
                  除外メッシュ: {analysisResult.summary.excluded_meshes}
                </p>
                <h4 style={{ fontSize: '13px', marginBottom: '8px' }}>ランク分布</h4>
                {Object.entries(analysisResult.summary.rank_distribution).map(([rank, count]) => (
                  <div key={rank} className="legend-item">
                    <div className="legend-color" style={{ backgroundColor: RANK_COLORS[rank] }}></div>
                    <span>{rank}: {count}件</span>
                  </div>
                ))}
                <div style={{ marginTop: '12px', fontSize: '10px', color: '#666', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                  データソース: 不動産情報ライブラリ（国土交通省）、PLATEAU（国土交通省）
                </div>
              </div>
            </div>
          )}

          {/* 選択地点の詳細 */}
          {selectedFeature && selectedFeature.properties && (
            <div className="panel-section">
              <div className="panel-section-header">選択地点詳細</div>
              <div className="panel-section-content">
                <div style={{
                  background: '#fff3e0',
                  border: '1px solid #ffb74d',
                  borderRadius: '4px',
                  padding: '8px',
                  marginBottom: '12px',
                  fontSize: '11px',
                  color: '#e65100'
                }}>
                  AIによる自動判定の為、参考にしてください
                </div>
                <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                  位置: {selectedFeature.properties.center_lat?.toFixed(4)}, {selectedFeature.properties.center_lon?.toFixed(4)}
                </p>
                {selectedFeature.properties.excluded ? (
                  <div className="total-score" style={{ background: '#ffebee' }}>
                    <span style={{ color: '#d32f2f', fontSize: '18px' }}>除外エリア</span>
                    <p style={{ fontSize: '12px', marginTop: '8px' }}>
                      {selectedFeature.properties.exclusion_reason || '除外条件に該当'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="total-score">
                      <span className="score" style={{ color: RANK_COLORS[selectedFeature.properties.rank || 'X'] }}>
                        {selectedFeature.properties.total_score}
                      </span>
                      <span className="rank">
                        【{selectedFeature.properties.rank || 'X'}】{RANK_LABELS[selectedFeature.properties.rank || 'X']}
                      </span>
                    </div>

                    {/* 総合判定理由 */}
                    {selectedFeature.properties.scores && (
                      <div style={{
                        background: '#e3f2fd',
                        border: '1px solid #90caf9',
                        borderRadius: '4px',
                        padding: '12px',
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#1565c0',
                        lineHeight: '1.6'
                      }}>
                        <strong style={{ display: 'block', marginBottom: '6px' }}>総合判定</strong>
                        {getOverallReason(
                          selectedFeature.properties.rank || 'X',
                          selectedFeature.properties.scores,
                          selectedFeature.properties.raw_data
                        )}
                      </div>
                    )}

                    {selectedFeature.properties.scores && (
                      <div style={{ marginTop: '16px' }}>
                        <h4 style={{ fontSize: '13px', marginBottom: '8px' }}>各指標スコアと判定理由</h4>
                        {[
                          { key: 'restriction_surface', label: '制限表面確保' },
                          { key: 'landuse', label: '用途地域適合' },
                          { key: 'downwash', label: 'ダウンウォッシュ' },
                          { key: 'population', label: '騒音影響' },
                          { key: 'emergency_facility', label: '緊急施設' },
                          { key: 'access', label: '交通アクセス' }
                        ].map(({ key, label }) => {
                          const score = selectedFeature.properties?.scores?.[key] || 0
                          const color = score >= 80 ? '#2E7D32' : score >= 60 ? '#7CB342' : score >= 40 ? '#FDD835' : score >= 20 ? '#FB8C00' : '#D32F2F'
                          const reason = getScoreReason(key, score, selectedFeature.properties?.raw_data)
                          return (
                            <div key={key} className="score-bar-container" style={{ marginBottom: '12px' }}>
                              <div className="score-bar-label">
                                <span>{label}</span>
                                <span>{score}点</span>
                              </div>
                              <div className="score-bar">
                                <div className="score-bar-fill" style={{ width: `${score}%`, background: color }}></div>
                              </div>
                              <p style={{ fontSize: '11px', color: '#666', marginTop: '4px', lineHeight: '1.4' }}>
                                {reason}
                              </p>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* 分析根拠（実データ） */}
                    {selectedFeature.properties.raw_data && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #ddd', paddingTop: '12px' }}>
                        <h4 style={{ fontSize: '13px', marginBottom: '8px', color: '#1976d2' }}>分析根拠（実データ）</h4>

                        {selectedFeature.properties.raw_data.landuse_type && (
                          <div className="data-item">
                            <span className="data-label">用途地域:</span>
                            <span className="data-value">{selectedFeature.properties.raw_data.landuse_type}</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.population_density !== undefined && (
                          <div className="data-item">
                            <span className="data-label">人口密度:</span>
                            <span className="data-value">{Math.round(selectedFeature.properties.raw_data.population_density)} 人/km²</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.nearest_hospital_distance !== undefined && (
                          <div className="data-item">
                            <span className="data-label">最寄り病院:</span>
                            <span className="data-value">{Math.round(selectedFeature.properties.raw_data.nearest_hospital_distance)} m</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.nearest_station_distance !== undefined && (
                          <div className="data-item">
                            <span className="data-label">最寄り駅:</span>
                            <span className="data-value">{Math.round(selectedFeature.properties.raw_data.nearest_station_distance)} m</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.building_max_height !== undefined && (
                          <div className="data-item">
                            <span className="data-label">周辺建物最大高:</span>
                            <span className="data-value">{Math.round(selectedFeature.properties.raw_data.building_max_height)} m</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.directions_clear !== undefined && (
                          <div className="data-item">
                            <span className="data-label">進入方向確保:</span>
                            <span className="data-value">{selectedFeature.properties.raw_data.directions_clear} 方向</span>
                          </div>
                        )}

                        {selectedFeature.properties.raw_data.restriction_evaluation && (
                          <div className="data-item">
                            <span className="data-label">制限表面評価:</span>
                            <span className="data-value">{selectedFeature.properties.raw_data.restriction_evaluation}</span>
                          </div>
                        )}

                        {/* データソース表示 */}
                        {selectedFeature.properties.raw_data.data_sources &&
                         selectedFeature.properties.raw_data.data_sources.length > 0 && (
                          <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                            <span>データソース: </span>
                            {selectedFeature.properties.raw_data.data_sources.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3D地図 */}
        <div className="map-container">
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

          {/* マップ上のポップアップ（右下固定） */}
          {selectedFeature && selectedFeature.properties && (
            <div
              className="map-popup"
              style={{
                position: 'absolute',
                right: 20,
                bottom: 20,
                zIndex: 1000
              }}
            >
              <button
                className="popup-close"
                onClick={() => { setSelectedFeature(null); setPopupPosition(null); }}
              >
                ×
              </button>

              <div className="popup-warning">
                AIによる自動判定の為、参考にしてください
              </div>

              <div className="popup-header">
                <span
                  className="popup-rank"
                  style={{ backgroundColor: RANK_COLORS[selectedFeature.properties.rank || 'X'] }}
                >
                  {selectedFeature.properties.rank || 'X'}
                </span>
                <span className="popup-score">
                  {selectedFeature.properties.excluded
                    ? '除外エリア'
                    : `${selectedFeature.properties.total_score}点 - ${RANK_LABELS[selectedFeature.properties.rank || 'X']}`
                  }
                </span>
              </div>

              {selectedFeature.properties.excluded ? (
                <div className="popup-content">
                  <p style={{ color: '#d32f2f' }}>
                    {selectedFeature.properties.exclusion_reason || '除外条件に該当'}
                  </p>
                </div>
              ) : (
                <div className="popup-content">
                  {/* 総合判定理由 */}
                  <div className="popup-reason">
                    {selectedFeature.properties.scores && getOverallReason(
                      selectedFeature.properties.rank || 'X',
                      selectedFeature.properties.scores,
                      selectedFeature.properties.raw_data
                    )}
                  </div>

                  {/* スコア一覧と判定理由 */}
                  {selectedFeature.properties.scores && (
                    <div className="popup-scores">
                      {[
                        { key: 'restriction_surface', label: '制限表面' },
                        { key: 'landuse', label: '用途地域' },
                        { key: 'downwash', label: 'ダウンウォッシュ' },
                        { key: 'population', label: '騒音影響' },
                        { key: 'emergency_facility', label: '緊急施設' },
                        { key: 'access', label: 'アクセス' }
                      ].map(({ key, label }) => {
                        const score = selectedFeature.properties?.scores?.[key] || 0
                        const color = score >= 80 ? '#2E7D32' : score >= 60 ? '#7CB342' : score >= 40 ? '#FDD835' : score >= 20 ? '#FB8C00' : '#D32F2F'
                        const reason = getScoreReason(key, score, selectedFeature.properties?.raw_data)
                        return (
                          <div key={key} className="popup-score-detail">
                            <div className="popup-score-item">
                              <span className="popup-score-label">{label}</span>
                              <div className="popup-score-bar">
                                <div style={{ width: `${score}%`, backgroundColor: color }}></div>
                              </div>
                              <span className="popup-score-value">{score}</span>
                            </div>
                            <p className="popup-score-reason">{reason}</p>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* 主要データ */}
                  {selectedFeature.properties.raw_data && (
                    <div className="popup-data">
                      {selectedFeature.properties.raw_data.landuse_type && (
                        <span>用途: {selectedFeature.properties.raw_data.landuse_type}</span>
                      )}
                      {selectedFeature.properties.raw_data.population_density !== undefined && (
                        <span>人口密度: {Math.round(selectedFeature.properties.raw_data.population_density)}人/km²</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="popup-footer">
                <small style={{ color: '#999' }}>
                  {selectedFeature.properties.center_lat?.toFixed(4)}, {selectedFeature.properties.center_lon?.toFixed(4)}
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* フッター */}
      <footer className="footer">
        <span>出典: 国土地理院 | PLATEAU（国土交通省）| 不動産情報ライブラリ（国土交通省）</span>
        <span>つくば市バーティポート適地分析GISシステム v1.0.0</span>
      </footer>
    </div>
  )
}

export default App
